import React, { useId, useMemo, useRef, useState } from 'react';
import { ArrowRightLeft } from 'lucide-react';
import { useLocation, useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import { Head4 } from '../../lib/font';
import { colors } from '../../lib/colors';
import SvgIcon from '../SvgIcon';
import useSingleQuery from '../../hook/useSingleQuery';
import {
    fetchMembershipForMember,
    MemberMembershipDataType,
    MemberScheduleDataType,
    PaymentHistoryType,
} from '../../services/useMembershipQueryService';
import MemberMembershipManageComponents from './MemberMembershipManageComponents';
import MemberMembershipManageHistory from './MemberMembershipManageHistory';
import MemberMembershipChangeLog from './MemberMembershipChangeLog';
import Modal from '../modal/Modal';
import ModalMembershipUpdate from './ModalMembershipUpdate';
import ModalDeleteAlert from './ModalDeleteAlert';
import ModalRefundAndUpgrade from './ModalRefund';
import { getItemWithExpiration } from '../../lib/utils';
import useMembershipService from '../../services/useMembershipService';
import { useQueryClient } from '@tanstack/react-query';
import ModalPartialPayment from '../../scenes/Settlement/ModalPartialPayment';
import ModalPaymentHistoryUpdate from './ModalPaymentHistoryUpdate';
import ScheduleDetailModal from '../Schedule/DetailModal/ScheduleDetailModal';
import ModalChangePartialPay from './ModalChangePartialPay';
import ModalDeleteError from './ModalDeleteError';
import ModalConvertToGroupTicket from './ModalConvertToGroupTicket';
import BusinessInfoFooter from '../BusinessInfoFooter';
import ModalGroupTicketSessionRegister from './ModalGroupTicketSessionRegister';
import ModalGroupBookingHistoryAction from './ModalGroupBookingHistoryAction';
import ModalMembershipChange from '../../scenes/ClassPass/ClassPassDetail/ClassPassHistory/ModalMembershipChange';
import ModalRegisterPass from '../../scenes/ClassPass/ModalRegisterPass/ModalRegisterPass';
import { MembershipTemplateDataType } from '../../services/useMembershipTemplateQueryService';
import WithdrawnMemberReadOnlyNotice from './WithdrawnMemberReadOnlyNotice';
import { isMemberRecordReadOnly } from '../../features/publicMember/types';
import SharedMembershipSection from './SharedMembershipSection';
import ModalMembershipConversion, {
    MembershipConversionOptionState,
} from './ModalMembershipConversion';
import { SharePoolScope } from '../../features/sharedMembership/types';
import {
    isSharedMembershipLifecycleMutationBlocked,
    readShareLifecycle,
    resolveShareEditability,
    resolveSharedMembershipEditScope,
    resolveSharedMembershipUpdateFailure,
    useSharePoolByScope,
} from '../../features/sharedMembership';
import { useCenterFeatureFlags } from '../../hooks/useCenterFeatureFlags';
import { isActionableMembershipDetail, normalizeMembershipDetail } from '../../lib/membershipDetailGuard';
import { buildRefundModalProps } from '../../features/sharedMembership/refundCandidates';
import MembershipLifecycleNotice from './MembershipLifecycleNotice';
import LessonMovePanel from '../../scenes/Settlement/SessionReportLessonMove';
import { useSessionReportLessonsAvailability } from '../../features/sessionReport/hooks';
import MembershipPauseSection from '../MembershipPause/MembershipPauseSection';
import {
    getMembershipLifecycle,
    isImmutableRefundRow,
    isLifecycleActionBlocked,
    REFUND_ROW_IMMUTABLE_TOOLTIP,
} from '../../lib/membershipLifecycle';

type Props = {
    createAlert: any;
    createTwoButtonAlert: any;
};

const MemberMembershipManagePage = ({ createAlert, createTwoButtonAlert }: Props) => {
    const queryClient = useQueryClient();
    const location = useLocation();
    const navigate = useNavigate();
    const membershipId = location.state?.membershipId;
    const memberNameFromState = location.state?.memberName;
    const coachName = location.state?.coachName;
    const memberId = location.state?.memberId;
    const coachId = location.state?.coachId;
    /** 'pt' 또는 'group' — SalesListPage/UnpaidListPage 등에서 navigate 시 전달 */
    const source: 'pt' | 'group' = location.state?.source === 'group' ? 'group' : 'pt';
    const centerIdFromState: string | undefined = location.state?.centerId;
    const authObjectCenterId = getItemWithExpiration('authObject')?.centerId;
    const centerId = centerIdFromState || authObjectCenterId;
    const isGroup = source === 'group';

    const {
        refinedResult: membershipData,
        rawData: membershipResponse,
        isLoading: isMembershipLoading,
        isFetching: isMembershipFetching,
        refresh: refreshMembership,
    } = useSingleQuery({
        fetchFn: fetchMembershipForMember,
        params: {
            membershipId,
            source,
            centerId,
        },
        enabled: !!membershipId,
    });
    const membership = normalizeMembershipDetail(membershipData?.data) as MemberMembershipDataType;
    /**
     * 공유 상품(TEMPLATE) 발급 건의 **현재** 공유 단계. 서버가 매번 다시 계산해 내려준다.
     *
     * 예전에는 `sharePolicySnapshot.enabled` 하나로 "공유 상품으로 발급됐다"(영구 사실)와
     * "지금 막아야 한다"(현재 상태)를 겸했다. 그래서 공유를 몇 분 만에 끄고 참여자가 0명이어도
     * 수정·재공유가 영구히 잠겼다. 이제 추론하지 않고 서버 판정을 그대로 읽는다.
     *
     * 키가 없으면(구버전 서버 / 서버 판정 실패) `resolveShareEditability` 가 예전 추론으로
     * 폴백한다 — 더 엄격한 쪽이라 프론트가 먼저 배포돼도 안전하다.
     */
    const shareLifecycle = useMemo(
        // useSingleQuery 는 응답 타입을 모른다(제네릭 없이 useQuery 를 부른다). 본문만 꺼내
        // 방어적으로 읽는다 — readShareLifecycle 이 형태가 어긋나면 null 을 돌린다.
        () => readShareLifecycle((membershipResponse as { data?: unknown } | undefined)?.data),
        [membershipResponse],
    );
    const shareEditability = useMemo(
        () =>
            resolveShareEditability({
                lifecycle: shareLifecycle,
                sharePolicySnapshotEnabled: membership?.sharePolicySnapshot?.enabled === true,
            }),
        [shareLifecycle, membership?.sharePolicySnapshot?.enabled],
    );
    const isReadOnly = isMemberRecordReadOnly({
        appAccountStatus: membership?.appAccountStatus,
        appWithdrawnAt: membership?.appWithdrawnAt,
        appWithdrawalAuthStatus: membership?.appWithdrawalAuthStatus,
        isReadOnly: location.state?.isReadOnly === true || membership?.isReadOnly === true,
    });

    // 회원명: 라우터 state 우선, 없으면 상세 응답 값 사용.
    //   state 가 비는 경로 — 새로고침/직접 링크, 그리고 매출탭에서 넘어온 그룹 수강권
    //   (결제레코드 memberName 스냅샷이 ''이던 레거시 데이터)
    const memberName = memberNameFromState || membership?.memberName || '';

    /**
     * 종료(소프트 삭제)된 수강권 표식.
     *
     * 지금까지 이 화면은 종료된 수강권에 대해 404 였다. 서버가 상세를 열면서 **왜 종료됐고
     * 무엇이 막혀 있는지**를 응답에 실어 준다. 화면은 그 목록만 보고 잠근다 — 자체 규칙을
     * 만들면 서버가 실제로 거부하는 것과 갈린다(P2).
     *
     * 구 백엔드에서는 이 필드들이 없어 `isEnded=false` 가 되고 기존 동작 그대로다.
     */
    const lifecycle = useMemo(() => getMembershipLifecycle(membership), [membership]);
    /**
     * 종료된 수강권의 수업 이력은 **읽기 전용**이다(P16 / U10).
     *
     * 서버는 `SCHEDULE_ACTIONS` 를 lifecycleBlockedActions 에 넣지만 실제로 막지는 않는다 —
     * 같은 조작이 오늘도 캘린더에서 가능하고 서버 경계 강화는 별건이다. 즉 이건 순전히
     * 프론트 규율이라, 화면이 스스로 지키지 않으면 아무도 막아 주지 않는다.
     */
    const lifecycleEndedScheduleReadOnly =
        lifecycle.isEnded || isLifecycleActionBlocked(lifecycle, 'SCHEDULE_ACTIONS');
    // 잘못 차감된 수업을 다른 수강권으로 옮기기(세션 현황과 같은 기능). 개인 수업 수강권·서버에 기능이 있을 때만.
    const lessonMoveAvailability = useSessionReportLessonsAvailability(source === 'pt' ? centerId : undefined);
    const [showLessonMove, setShowLessonMove] = useState(false);
    const lessonMovePanelId = useId();
    const canShowLessonMove = source === 'pt' && centerId && membershipId && lessonMoveAvailability.data === 'available';
    const isActive = membership?.status === 'active';
    const isRemainSession = membership?.remainSession > 0;
    const beforeMembershipTemplate = useMemo<MembershipTemplateDataType | null>(() => {
        if (!membership || isGroup) return null;
        return {
            _id: membership.membershipTemplateId,
            period: membership.period,
            isAvailable: true,
            BGColor: membership.BGColor || ['#40CE96', '#1B5AD4'],
            isDeleted: false,
            isCanceled: false,
            name: membership.name,
            pricePerSchedule: membership.pricePerSchedule,
            totalPrice: membership.totalPrice,
            totalSession: membership.totalSession,
            issuer: membership.issuer || { issuerRole: 'admin' },
            memo: membership.memo || '',
            validDays: Math.max(dayjs(membership.expirationDate).diff(dayjs(membership.activationDate), 'day'), 0),
            createdAt: membership.createdAt,
            updatedAt: membership.updatedAt,
            __v: membership.__v,
            recentUsedTime: membership.updatedAt,
        };
    }, [isGroup, membership]);

    /**
     * 공유 수강권 섹션이 어떤 원본을 볼지 결정한다.
     * PT는 담당 강사가 있어야 참여 후보를 담당 강사로 좁힐 수 있다.
     * 필요한 값이 없으면 섹션 자체를 렌더링하지 않는다.
     */
    const sharePoolScope = useMemo<SharePoolScope | null>(() => {
        if (!membershipId || !membership?.memberId) return null;
        if (isGroup) return { sourceType: 'MEMBER_TICKET', memberTicketId: membershipId };
        if (!membership?.coachId) return null;
        return { sourceType: 'MEMBERSHIP', membershipId };
    }, [isGroup, membership?.coachId, membership?.memberId, membershipId]);

    /**
     * '수강권 전환' 버튼과 결제 이력 잠금이 공유 여부를 알아야 한다.
     * SharedMembershipSection과 쿼리 키가 같아 캐시를 공유하므로 요청은 늘지 않는다.
     */
    const { isSharedMembershipReadEnabled, isSharedMembershipCreatePoolEnabled } =
        useCenterFeatureFlags();
    const sharePoolQuery = useSharePoolByScope({
        scope: sharePoolScope || { sourceType: 'MEMBERSHIP', membershipId: '' },
        enabled: isSharedMembershipReadEnabled && !!sharePoolScope,
    });

    /**
     * '그룹 전환'과 '회차 공유'는 이름만으로 구분되지 않아 버튼을 '수강권 전환' 하나로 합쳤다.
     * 어느 쪽을 지금 고를 수 있는지는 여기서 판정하고, 고를 수 없는 이유까지 모달에 넘겨
     * 사용자가 "왜 이건 안 되지"에서 막히지 않게 한다.
     */
    const hasSharePool = !!sharePoolQuery.data;
    const isSharingActive = sharePoolQuery.data?.status === 'ACTIVE';
    const groupConversionState = useMemo<MembershipConversionOptionState>(() => {
        if (isGroup) return { isAvailable: false, unavailableReason: '이미 그룹 수강권입니다.' };
        if (membership?.status !== 'active') {
            return { isAvailable: false, unavailableReason: '활성 수강권만 전환할 수 있습니다.' };
        }
        if ((membership?.remainSession ?? 0) <= 0) {
            return { isAvailable: false, unavailableReason: '남은 회차가 없어 전환할 수 없습니다.' };
        }
        if (isSharingActive) {
            // 그룹 전환은 원본을 만료시킨다. 공유 참여자가 붙은 채로 실행하면 참여자 예약이 갈 곳을 잃는다.
            return {
                isAvailable: false,
                unavailableReason: '회차를 공유 중인 수강권입니다. 공유를 먼저 종료한 뒤 전환해주세요.',
            };
        }
        return { isAvailable: true };
    }, [isGroup, isSharingActive, membership?.remainSession, membership?.status]);

    const shareConversionState = useMemo<MembershipConversionOptionState>(() => {
        // 서버가 종료된 수강권의 공유 provisioning 쓰기를 409 로 막는다(L6). 고를 수 없게 한다.
        if (isLifecycleActionBlocked(lifecycle, 'SHARE_PROVISION')) {
            return {
                isAvailable: false,
                unavailableReason: '종료된 수강권은 회차 공유로 전환할 수 없습니다.',
            };
        }
        if (!isSharedMembershipReadEnabled || !isSharedMembershipCreatePoolEnabled) {
            return {
                isAvailable: false,
                unavailableReason: '센터에 회차 공유 기능이 아직 열리지 않았습니다.',
            };
        }
        if (!sharePoolScope) {
            return {
                isAvailable: false,
                unavailableReason: '담당 강사가 지정된 수강권만 공유할 수 있습니다.',
            };
        }
        /**
         * 공유 시작 모달은 SharedMembershipSection이 "풀 없음" 상태일 때만 그린다.
         * 조회 중·조회 실패·공유 상품 발급 상태에서는 그 자리에 다른 화면이 있어,
         * 여기서 고를 수 있게 두면 [다음]을 눌러도 아무 일도 일어나지 않는다.
         */
        if (sharePoolQuery.isLoading) {
            return {
                isAvailable: false,
                unavailableReason: '공유 정보를 확인하는 중입니다. 잠시 후 다시 시도해주세요.',
            };
        }
        if (sharePoolQuery.isError) {
            return {
                isAvailable: false,
                unavailableReason: '공유 정보를 불러오지 못했습니다. 새로고침 후 다시 시도해주세요.',
            };
        }
        if (hasSharePool) {
            return {
                isAvailable: false,
                unavailableReason: isSharingActive
                    ? '이미 회차를 공유 중입니다. 위 공유 수강권 카드에서 관리하세요.'
                    : '이 수강권은 공유를 종료한 이력이 있습니다. 위 공유 수강권 카드에서 확인하세요.',
            };
        }
        if (membership?.sharePolicySnapshot?.enabled === true) {
            return {
                isAvailable: false,
                unavailableReason:
                    '공유 상품으로 발급된 수강권입니다. 위 공유 수강권 카드에서 설정 상태를 확인해주세요.',
            };
        }
        if (membership?.status !== 'active') {
            return { isAvailable: false, unavailableReason: '활성 수강권만 공유할 수 있습니다.' };
        }
        return { isAvailable: true };
    }, [
        hasSharePool,
        isSharedMembershipCreatePoolEnabled,
        isSharedMembershipReadEnabled,
        isSharingActive,
        lifecycle,
        membership?.sharePolicySnapshot?.enabled,
        membership?.status,
        sharePoolQuery.isError,
        sharePoolQuery.isLoading,
        sharePoolScope,
    ]);
    /**
     * 공유 중이거나 공유 상품으로 발급된 수강권은 결제 이력을 일반 수정·삭제할 수 없다
     * (서버가 409 SHARED_MEMBERSHIP_PAYMENT_HISTORY_MUTATION_BLOCKED 로 막는다).
     * 모달을 열어두면 저장이 무반응으로만 보이므로 진입 자체를 막고 이유를 알린다.
     *
     * PT 한정이다. 그룹(member-ticket-payments) 경로에는 서버 가드가 없어 여기서 막으면
     * 서버가 허용하는 편집을 화면에서만 없애고, 안내 문구까지 사실과 달라진다.
     *
     * ⚠️ 종료 후 **일반 수정**은 열렸지만 **결제 이력**은 아니다. 서버의
     * `assertActiveSharedMembershipPaymentHistoryMutationAllowed` 는 여전히
     * `sharePolicySnapshot.enabled` 표식만 보고 영구 차단한다. 그래서 여기만은
     * `shareEditability` 로 바꾸지 않는다 — 바꾸면 모달을 열어놓고 저장에서 409가 난다.
     */
    const isSharedPaymentHistoryLocked =
        !isGroup &&
        (sharePoolQuery.data?.status === 'ACTIVE' ||
            membership?.sharePolicySnapshot?.enabled === true);
    /**
     * 지금 이 수강권을 어디까지 고칠 수 있는가 — 전부 / 일부 / 전면 차단.
     *
     * 진입 버튼과 모달이 같은 판정을 따로 만들지 않도록 한 곳에서만 정한다.
     * 공유 **중**에도 서버가 허용하는 필드가 있으면 모달을 열고 나머지를 잠근다.
     * 서버가 목록을 안 주는 구버전에서는 교집합이 비어 예전처럼 전면 차단이 된다.
     */
    const membershipEditScope = resolveSharedMembershipEditScope({
        source,
        isActiveSharePool: isSharingActive,
        editability: shareEditability,
    });
    const membershipUpdateRestriction =
        membershipEditScope.mode === 'BLOCKED' ? membershipEditScope.restriction : null;
    const membershipSharedActiveEdit =
        membershipEditScope.mode === 'LIMITED'
            ? {
                  editableFields: membershipEditScope.editableFields,
                  notice: membershipEditScope.notice,
              }
            : null;

    const [authorization] = useState(getItemWithExpiration('authorization'));
    // 스케줄 상세 모달 데이터
    const [detailObject, setDetailObject] = useState<any>();
    // 분할결제 할때 사용 — ref로 관리해야 useMembershipService 클로저에서 최신값 읽힘
    const isSplitPaymentRef = useRef(false);

    const { updateMemberShipMutation, deleteMemberShipMutation } = useMembershipService({
        onUpdatedMemberShipSuccess: () => {
            if (isSplitPaymentRef.current) {
                createAlert('분할결제 설정 완료!', '분할결제가 설정되었어요!', false);
                isSplitPaymentRef.current = false;
            } else if (isActive) {
                createAlert(
                    '수강권 만료 완료',
                    '수강권이 만료되었습니다!\n수강권의 사용 회차가 남아있을 경우, 재활성화가 가능해요!',
                    true
                );
            } else {
                createAlert('수강권 재활성화 완료!', '수강권이 재활성화 되었어요!', false);
            }

            queryClient.invalidateQueries({
                queryKey: ['getMembershipForMemberQuery'],
            });
            // 변경 이력 패널 갱신
            queryClient.invalidateQueries({ queryKey: ['membershipChangeLog'] });
        },
        onUpdatedMemberShipFail: (status, data) => {
            isSplitPaymentRef.current = false;

            // 종료 후 수정에서만 나오는 409 두 가지(발급 원본 변경 / 회차 축소 충돌)를 먼저 본다.
            // 아는 코드가 아니면 null 이라 아래 기존 처리가 그대로 이어진다.
            const updateFailure = resolveSharedMembershipUpdateFailure({ status, data });
            if (updateFailure) {
                createAlert(updateFailure.title, updateFailure.message, true);
                return;
            }

            if (isSharedMembershipLifecycleMutationBlocked(data)) {
                // 이 mutation 은 만료·재활성화·분할결제 — 전부 보호 필드를 보낸다. 그래서
                // 모달이 일부만 열린 상태(LIMITED)에서도 여기서는 여전히 막힌다.
                // 진입 차단 문구가 아니라 409 전용 문구를 쓴다.
                createAlert(
                    membershipEditScope.lifecycleBlockedNotice?.title ?? '공유 중에는 변경할 수 없어요',
                    membershipEditScope.lifecycleBlockedNotice?.message ??
                        '공유 수강권 정보가 변경되었습니다. 화면을 새로고침한 뒤 공유 상태를 확인해주세요.',
                    true
                );
                return;
            }

            if (status === 412) {
                createAlert(
                    '수강권 관리 권한이 없어요',
                    '센터 관리자에게 수강권 수정 권한을 요청한 뒤 다시 시도해주세요.',
                    true
                );
                return;
            }
            createAlert(
                '수강권 변경 실패',
                status
                    ? `요청을 처리하지 못했어요.\n잠시 후 다시 시도하거나, 계속되면 고객센터로 문의해주세요. (오류 코드 ${status})`
                    : '네트워크 연결을 확인한 뒤 다시 시도해주세요.',
                true
            );
        },
        onDeleteMemberShipSuccess: () => {
            createAlert(
                '수강권 삭제 완료',
                '수강권이 삭제되었습니다!\n수강권이 삭제되었으니, 회원의 수강권을 다시 등록해주세요!',
                false
            );
            // PT 수강권 쿼리 무효화
            queryClient.invalidateQueries({
                queryKey: ['getAllMembershipForMemberQuery'],
            });
            // 그룹 수강권 쿼리 무효화 — MemberGroupTab의 useMemberTickets 캐시 갱신
            queryClient.invalidateQueries({
                queryKey: ['groupTicket', 'member', centerId, memberId],
            });

            // 뒤로가기
            window.history.back();
        },
        onDeleteMemberShipFail: (status, data) => {
            const failure = resolveSharedMembershipUpdateFailure({ status, data });
            if (failure) {
                setIsDeleteModalVisible(false);
                createAlert(failure.title, failure.message, true);
            }
        },
    });

    const [selectedPaymentHistory, setSelectedPaymentHistory] = useState<PaymentHistoryType | undefined>(undefined);

    //수강권 수정하기 모달
    const [isMembershipUpdateModalVisible, setIsMembershipUpdateModalVisible] = useState(false);
    // 변경 이력 모달
    const [isChangeLogModalVisible, setIsChangeLogModalVisible] = useState(false);
    // 삭제 경고 모달
    const [isDeleteModalVisible, setIsDeleteModalVisible] = useState(false);
    // 환불, 업그레이드 모달
    const [isRefundAndUpgradeModalVisible, setIsRefundAndUpgradeModalVisible] = useState(false);
    // 환불인지 업그레이드인지
    const [refundAndUpgradeType, setRefundAndUpgradeType] = useState<'refund' | 'upgrade'>('refund');
    // 미수금 결제 모달
    const [isUnpaidPaymentModalVisible, setIsUnpaidPaymentModalVisible] = useState(false);
    // 결제 히스토리 클릭 모달
    const [isPaymentHistoryModalVisible, setIsPaymentHistoryModalVisible] = useState(false);
    // 스케줄 상세 모달
    const [isVisibleScheduleDetail, setIsVisibleScheduleDetail] = useState(false);
    // 분할결제 여부 묻는 모달
    const [isPartialPaymentModalVisible, setIsPartialPaymentModalVisible] = useState(false);
    // 삭제 에러 여부 모달
    const [isDeleteErrorModalVisible, setIsDeleteErrorModalVisible] = useState(false);
    // PT → 그룹 전환 모달
    const [isConvertModalVisible, setIsConvertModalVisible] = useState(false);
    // 수강권 전환 선택 모달 (그룹 전환 / 회차 공유)
    const [isConversionChooserVisible, setIsConversionChooserVisible] = useState(false);
    /**
     * 공유 시작 모달은 SharedMembershipSection이 들고 있다(참여자 추가·부분 실패 처리까지 한 곳에서).
     * 값을 올려 여는 신호만 보낸다. 0은 "아직 요청 없음"이라 화면 진입 시 모달이 뜨지 않는다.
     */
    const [shareConvertRequestKey, setShareConvertRequestKey] = useState(0);
    // 그룹 수강권 수업 등록 모달
    const [isGroupSessionRegisterVisible, setIsGroupSessionRegisterVisible] = useState(false);
    // 그룹 회차 영향 기록 액션 모달
    const [selectedGroupBookingHistory, setSelectedGroupBookingHistory] = useState<MemberScheduleDataType | null>(null);
    // 단일 수강권 상품변경 모달
    const [isProductChangeModalVisible, setIsProductChangeModalVisible] = useState(false);
    // 수강권 상품변경 중 새 상품 생성 모달
    const [isRegisterPassModalVisible, setIsRegisterPassModalVisible] = useState(false);

    const onClickRegisterPtSchedule = () => {
        if (!membership || !memberId) {
            createAlert('수업 등록 불가', '수강권 정보를 불러온 뒤 다시 시도해주세요.', true);
            return;
        }

        const targetCoachId = membership.coachId || coachId;
        const targetCoachName = membership.coachName || coachName;

        if (!targetCoachId) {
            createAlert('담당 강사 확인 필요', '수강권의 담당 강사 정보를 찾을 수 없습니다.', true);
            return;
        }

        navigate('/schedule/create', {
            state: {
                type: 'oneSchedule',
                selectedSchedule: {
                    memberId,
                    coachId: targetCoachId,
                    coachName: targetCoachName,
                    membershipId: membership._id,
                    scheduleStartTime: dayjs().format('YYYY-MM-DD HH:mm'),
                },
                calendarType: 'daily',
                calendarSelectedDay: dayjs().format('YYYY-MM-DD'),
                calendarScrollY: 0,
                returnPath: '/member/detail/membership',
                returnState: {
                    membershipId: membership._id,
                    memberName,
                    coachName: targetCoachName,
                    memberId,
                    coachId: targetCoachId,
                    source,
                    centerId,
                },
            },
        });
    };

    // 조회 실패 응답은 기존 useSingleQuery 정제 과정에서 []가 된다. 이를 수강권처럼 렌더링하면
    // undefined/undefined회와 오늘 날짜가 노출되고 삭제·환불 같은 파괴적 액션까지 활성화된다.
    if (!isMembershipLoading && !isActionableMembershipDetail(membership)) {
        return (
            <div className="flex min-h-screen flex-col items-center bg-[#F4F5F7] pt-[112px]">
                <div className="w-full max-w-[720px] px-[20px]">
                    <button
                        type="button"
                        className="mb-[24px] flex items-center gap-[12px] text-[16px] font-semibold text-[#374151] hover:opacity-70"
                        onClick={() => window.history.back()}
                    >
                        <SvgIcon name="SvgLeftStrokeArrow" stroke={colors.LAVEL_4} size={18} fill="none" />
                        돌아가기
                    </button>
                    <div className="rounded-[18px] border border-solid border-[#FED7AA] bg-white px-[36px] py-[40px] text-center shadow-sm">
                        <div className="mb-[10px] text-[21px] font-bold text-[#9A3412]">
                            수강권 정보를 불러올 수 없습니다
                        </div>
                        <div className="mx-auto mb-[24px] max-w-[560px] text-[14px] leading-[22px] text-[#6B7280]">
                            인계 전 수강권을 가리키는 과거 링크이거나 삭제된 수강권일 수 있습니다. 잘못된 수강권을 수정하거나 환불하지 않도록 관리 기능을 잠갔습니다.
                        </div>
                        <button
                            type="button"
                            disabled={isMembershipFetching}
                            className="h-[44px] rounded-[9px] bg-[#374151] px-[22px] text-[14px] font-bold text-white hover:bg-[#1F2937] disabled:cursor-wait disabled:opacity-60"
                            onClick={() => refreshMembership()}
                        >
                            {isMembershipFetching ? '다시 확인하는 중' : '다시 확인'}
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="flex flex-col pt-[112px] items-center bg-[#F4F5F7]">
            <div className="flex flex-col w-[1192px] item-center justify-center ">
                <div
                    className="flex flex-row items-center cursor-pointer transition-opacity duration-200 hover:opacity-70 mb-[24px]"
                    onClick={() => window.history.back()}
                >
                    <div className="flex items-center mr-[16px]">
                        <SvgIcon name="SvgLeftStrokeArrow" stroke={colors.LAVEL_4} size={20} fill="none" />
                    </div>
                    <Head4>수강권 정보</Head4>
                </div>
                {/*
                  공유 요약 배지는 없앴다. 공유 섹션 자체가 정보 카드 바로 아래로 올라와
                  같은 화면에 보이므로, 배지는 같은 말을 두 번 하고 '공유 관리로 이동' 스크롤
                  점프만 남기는 우회로가 된다.
                */}
                {isReadOnly && (
                    <div style={{ marginBottom: 20 }}>
                        {/* 이 화면에서는 원매출 환불만 예외로 열려 있다(G4). 그 사실을 배너가 말한다. */}
                        <WithdrawnMemberReadOnlyNotice canRefundOriginalSales />
                    </div>
                )}
                {lifecycle.isEnded && (
                    <div style={{ marginBottom: 20 }}>
                        <MembershipLifecycleNotice
                            lifecycle={lifecycle}
                            onClickCurrentMembership={
                                lifecycle.currentMembershipId
                                    ? () =>
                                          navigate('/member/detail/membership', {
                                              state: {
                                                  membershipId: lifecycle.currentMembershipId,
                                                  memberName,
                                                  coachName: membership?.coachName || coachName,
                                                  memberId: membership?.memberId || memberId,
                                                  coachId: membership?.coachId || coachId,
                                                  source,
                                                  centerId,
                                              },
                                          })
                                    : undefined
                            }
                        />
                    </div>
                )}
                <MemberMembershipManageComponents
                    membership={membership}
                    lifecycle={lifecycle}
                    memberName={memberName}
                    coachName={coachName}
                    onClickUpdate={() => {
                        if (membershipUpdateRestriction) {
                            createAlert(
                                membershipUpdateRestriction.title,
                                membershipUpdateRestriction.message,
                                true
                            );
                            return;
                        }
                        setIsMembershipUpdateModalVisible(true);
                    }}
                    onClickDelete={() => {
                        setIsDeleteModalVisible(true);
                    }}
                    createAlert={createAlert}
                    onClickRefund={() => {
                        setIsRefundAndUpgradeModalVisible(true);
                        setRefundAndUpgradeType('refund');
                    }}
                    onClickUpgrade={() => {
                        setIsRefundAndUpgradeModalVisible(true);
                        setRefundAndUpgradeType('upgrade');
                    }}
                    onClickExpire={() => {
                        // 종료 시각은 서버가 찍는다.
                        //   클라 시계로 만든 reRegistrationEndedAt 을 보내면, PC 시계가 서버보다 조금이라도
                        //   앞선 사용자는 서버 검증(Joi date().max('now'))에 걸려 400 → 만료 버튼이 무반응이 된다.
                        //   서버는 값이 없으면 자기 시각으로 기록하므로 사유만 보내면 충분하다.
                        updateMemberShipMutation.mutate({
                            membershipId: membershipId,
                            status: 'expired',
                            reRegistrationEndReason: 'MANUAL_NORMAL',
                            accessToken: authorization,
                            source,
                            centerId,
                        });
                    }}
                    onClickRow={(row) => {
                        // 신규 환불 API 가 만든 환불행은 서버가 수정·삭제를 409 로 막는다(P14).
                        // 목록 쪽에서도 진입을 막지만, 모달을 여는 경로가 여기 하나뿐이라
                        // 최종 관문을 여기에 둔다.
                        if (isImmutableRefundRow(row)) {
                            createAlert('수정할 수 없는 내역', REFUND_ROW_IMMUTABLE_TOOLTIP, true);
                            return;
                        }

                        if (isSharedPaymentHistoryLocked) {
                            /**
                             * 두 축을 갈라서 안내한다. 서버 가드는 "활성 풀이 있거나
                             * sharePolicySnapshot.enabled 표식이 있으면" 막는데, 표식 쪽은
                             * **영구**다(종료 후 수정이 열린 지금도 결제 이력은 그대로 막힌다).
                             * 그런데 공유 종료는 되돌릴 수 없다 — 열리지도 않을 종료를 권하면
                             * 관리자는 공유를 없애고도 아무것도 못 고치게 된다.
                             */
                            createAlert(
                                '결제 이력 수정 불가',
                                shareEditability.isTemplateIssued
                                    ? '공유 상품으로 발급된 수강권의 결제 이력은 수정·삭제할 수 없습니다.\n공유를 종료해도 열리지 않으니 운영 담당자에게 문의해주세요.'
                                    : '공유 중인 수강권의 결제 이력은 수정·삭제할 수 없습니다.\n공유를 종료한 뒤 다시 시도해주세요.',
                                true,
                            );
                            return;
                        }
                        setIsPaymentHistoryModalVisible(true);
                        setSelectedPaymentHistory(row);
                    }}
                    onClickReactivation={() => {
                        if (!isRemainSession) {
                            createAlert(
                                '회차 조절이 필요해요!',
                                `만료된 수강권을 활성화 시키려면 ‘수강권 수정하기’를 눌러\n‘총 회차’를 늘려주세요!`,
                                true
                            );
                        } else {
                            updateMemberShipMutation.mutate({
                                membershipId: membership._id,
                                status: 'active',
                                accessToken: authorization,
                                source,
                                centerId,
                            });
                        }
                    }}
                    onClickUnpaidPayment={() => {
                        setIsUnpaidPaymentModalVisible(true);
                    }}
                    onClickPartialPayment={() => {
                        setIsPartialPaymentModalVisible(true);
                    }}
                    onClickChangeLog={() => setIsChangeLogModalVisible(true)}
                    onDeleteError={() => {
                        setIsDeleteErrorModalVisible(true);
                    }}
                    onClickChangeProduct={
                        !isGroup && beforeMembershipTemplate
                            ? () => setIsProductChangeModalVisible(true)
                            : undefined
                    }
                    onClickConvert={
                        // 둘 다 고를 수 없으면 버튼을 아예 내린다 — 눌러도 막힌 카드만 보이는 건 시간 낭비다.
                        groupConversionState.isAvailable || shareConversionState.isAvailable
                            ? () => setIsConversionChooserVisible(true)
                            : undefined
                    }
                    sharedSlot={
                        sharePoolScope && membership && centerId && membership.memberId ? (
                            <SharedMembershipSection
                                centerId={centerId}
                                scope={sharePoolScope}
                                ownerMemberId={membership.memberId}
                                ownerName={memberName}
                                coachId={membership.coachId}
                                coachName={membership.coachName || coachName}
                                sourceStatus={membership.status}
                                isTemplateSharedMembership={shareEditability.isTemplateIssued}
                                canReshare={shareEditability.canReshare}
                                isReadOnly={isReadOnly}
                                convertRequestKey={shareConvertRequestKey}
                                createAlert={createAlert}
                            />
                        ) : null
                    }
                    pauseSlot={
                        membership && membershipId ? (
                            <MembershipPauseSection
                                kind={source}
                                passId={membershipId}
                                centerId={centerId}
                                membership={membership as any}
                                // 종료된 수강권·탈퇴 회원은 배지·기록만(서버도 사용 중이 아니면 409)
                                isReadOnly={isReadOnly || lifecycle.isEnded}
                                createAlert={createAlert}
                                createTwoButtonAlert={createTwoButtonAlert}
                                onRefresh={() => refreshMembership()}
                            />
                        ) : null
                    }
                    onClickRegisterGroupSessions={isGroup ? () => setIsGroupSessionRegisterVisible(true) : undefined}
                    onClickRegisterPtSchedule={!isGroup ? onClickRegisterPtSchedule : undefined}
                    isReadOnly={isReadOnly}
                />
                {/* isSharedPool: 종료 후에도 사용 기록의 '회원' 열이 남아야 누가 썼는지 확인할 수 있다.
                    ACTIVE만 보면 종료하는 순간 참여자 수업이 소유자 것처럼 보인다. */}
                <MemberMembershipManageHistory
                    schedules={membership?.schedules}
                    // 종료된 수강권에는 schedules 가 오지 않는다. 서버가 조작 가능한 재료를 주지
                    // 않으므로 클릭 경로가 애초에 없고, 화면은 보관 이력을 읽기 전용으로 그린다(P16).
                    archivedSchedules={membership?.archivedScheduleHistory}
                    isLifecycleEnded={lifecycleEndedScheduleReadOnly}
                    isSharedPool={!!sharePoolQuery.data}
                    ownerName={memberName}
                    memberId={memberId}
                    coachId={coachId}
                    coachName={coachName}
                    membershipId={membershipId}
                    source={source}
                    isReadOnly={isReadOnly}
                    headerRight={canShowLessonMove && (
                        <button
                            type="button"
                            title="잘못 차감된 수업을 다른 수강권으로 옮기기"
                            aria-label="잘못 차감된 수업을 다른 수강권으로 옮기기"
                            aria-expanded={showLessonMove}
                            aria-controls={showLessonMove ? lessonMovePanelId : undefined}
                            onClick={() => setShowLessonMove((v) => !v)}
                            className="inline-flex h-[32px] items-center gap-[6px] whitespace-nowrap rounded-[8px] border border-solid border-[#D9E6FF] bg-white px-[10px] text-[13px] text-primary hover:bg-[#F4F7FF]"
                        >
                            <ArrowRightLeft size={14} aria-hidden="true" />
                            수업 옮기기
                        </button>
                    )}
                    headerContent={canShowLessonMove && showLessonMove && (
                        <div id={lessonMovePanelId} role="region" aria-label="수업 옮기기" className="mt-[12px] w-full">
                            <LessonMovePanel
                                centerId={centerId}
                                membership={{ membershipId, name: membership?.name ?? '이 수강권' }}
                                readOnly={isReadOnly || lifecycleEndedScheduleReadOnly}
                            />
                        </div>
                    )}
                    onClickRow={(row) => {
                        /**
                         * P16/R24 — 종료된 수강권의 일정 행에서는 **어떤 액션도 열지 않는다.**
                         *
                         * 서버가 조작 재료(`schedules`)를 안 주는 것이 1차 방어이고, 목록
                         * 컴포넌트가 onClickRow 를 떼는 것이 2차다. 이 가드는 3차다 —
                         * `archivedScheduleHistory` 가 현재 원본 스케줄 객체를 그대로 담고 있어
                         * (_id 포함) 실수로 이 콜백이 연결되면 ScheduleDetailModal 이 열려
                         * R24 가 막으려던 진입점이 되살아난다. 백엔드 projection 이 좁혀져도
                         * 이 원칙은 유지한다.
                         */
                        if (lifecycleEndedScheduleReadOnly) return;
                        if (source === 'group') {
                            setSelectedGroupBookingHistory(row);
                            return;
                        }
                        setDetailObject(row);
                        setIsVisibleScheduleDetail(true);
                    }}
                />
            </div>
            {!isReadOnly && isMembershipUpdateModalVisible && (
                <ModalMembershipUpdate
                    coachName={coachName}
                    memberName={memberName}
                    membership={membership}
                    setIsVisible={setIsMembershipUpdateModalVisible}
                    createAlert={createAlert}
                    createTwoButtonAlert={createTwoButtonAlert}
                    updateRestriction={membershipUpdateRestriction}
                    lifecycleBlockedNotice={membershipEditScope.lifecycleBlockedNotice}
                    sharedActiveEdit={membershipSharedActiveEdit}
                    settlementImpactFields={shareEditability.settlementImpactFields}
                    participantImpactFields={shareEditability.participantImpactFields}
                    sharedUsage={shareEditability.sharedUsage}
                    // 공유 중 회차는 줄이는 것만 된다. 늘리려는 관리자를 막고 끝내지 않고
                    // 추가 결제를 처리할 수 있는 업그레이드로 보낸다.
                    onRequestUpgrade={() => {
                        setIsRefundAndUpgradeModalVisible(true);
                        setRefundAndUpgradeType('upgrade');
                    }}
                />
            )}
            {!isReadOnly && isDeleteModalVisible && (
                <ModalDeleteAlert
                    setIsVisible={setIsDeleteModalVisible}
                    onClickDelete={() => {
                        deleteMemberShipMutation.mutate({
                            membershipId: membershipId,
                            accessToken: authorization,
                            source,
                            centerId,
                        });
                    }}
                />
            )}
            {/*
              P3 — 탈퇴 회원이어도 **환불 모달만** 연다.
              탈퇴 가드는 신규 원매출 환불 라우트 한 곳에서만 풀렸다(G4). 업그레이드는 그대로
              막혀 있으므로 열지 않는다. 여기서 통째로 막으면 개방된 유일한 경로에 도달할 수 없다.
              그룹 환불 라우트(memberTicket)는 이 변경에 포함되지 않아 그대로 막혀 있으므로
              !isGroup 으로 좁힌다(MB M2와 동일 규칙).
            */}
            {(!isReadOnly || (!isGroup && refundAndUpgradeType === 'refund')) &&
                isRefundAndUpgradeModalVisible && (
                <ModalRefundAndUpgrade
                    setIsVisible={setIsRefundAndUpgradeModalVisible}
                    membership={membership}
                    coachName={coachName}
                    memberName={memberName}
                    type={refundAndUpgradeType}
                    createAlert={createAlert}
                    // 인계된 수강권은 결제 원장이 원판매 수강권에 남아 있어 자기 totalPrice 가 0이다.
                    // 서버가 계산한 환불 후보를 넘겨, 인계 여부와 무관하게 이 화면에서 환불을 끝낸다.
                    // 후보가 없으면(= 인계 이력 없음) 기존 동작 그대로다.
                    {...(refundAndUpgradeType === 'refund'
                        ? // 그룹(회원권 티켓)은 이 백엔드 변경에 포함되지 않는다 — deletedContext /
                          // refundCandidates 를 안 내리고 탈퇴 가드도 그대로다. isReadOnly 는 회원
                          // 단위 값이라 그룹에도 실리므로 여기서 명시적으로 좁힌다(MB M3′와 동일 규칙).
                          buildRefundModalProps(membership, { isGroup })
                        : {})}
                />
            )}
            {!isReadOnly && isUnpaidPaymentModalVisible && (
                <ModalPartialPayment
                    setIsVisible={setIsUnpaidPaymentModalVisible}
                    membership={membership}
                    coachName={coachName}
                    memberName={memberName}
                    createAlert={createAlert}
                    onPostSplitPaymentSuccess={() => {
                        queryClient.invalidateQueries({
                            queryKey: ['getMembershipForMemberQuery'],
                        });
                    }}
                />
            )}
            {!isReadOnly && isPaymentHistoryModalVisible && (
                <ModalPaymentHistoryUpdate
                    paymentHistory={selectedPaymentHistory}
                    setIsVisible={setIsPaymentHistoryModalVisible}
                    createAlert={createAlert}
                    source={source}
                    centerId={centerId}
                    // 인계 계보 결제 목록은 다른 세대의 행도 섞어 보여준다. 그 행을 고치면
                    // 회차가 저쪽 문서에 적용되므로 모달이 미리 경고한다(P5 / A6).
                    viewingMembershipId={membership?._id}
                />
            )}
            {/* 종료된 수강권에서는 일정 상세(수정·취소 액션 보유)를 열지 않는다 — P16 4차 방어. */}
            {isVisibleScheduleDetail && !lifecycleEndedScheduleReadOnly && (
                <ScheduleDetailModal
                    modalVisible={isVisibleScheduleDetail}
                    setModalVisible={setIsVisibleScheduleDetail}
                    detailObject={detailObject}
                    createAlert={createAlert}
                    createTwoButtonAlert={createTwoButtonAlert}
                    calendarType={'daily'}
                    calendarSelectedDay={undefined}
                    isReadOnly={isReadOnly}
                />
            )}
            {!isReadOnly && isPartialPaymentModalVisible && (
                <ModalChangePartialPay
                    setIsVisible={setIsPartialPaymentModalVisible}
                    onClickChangePartialPay={() => {
                        isSplitPaymentRef.current = true;
                        updateMemberShipMutation.mutate({
                            membershipId: membershipId,
                            isSplitPayment: true,
                            accessToken: authorization,
                            source,
                            centerId,
                        });
                    }}
                />
            )}
            {!isReadOnly && isDeleteErrorModalVisible && <ModalDeleteError setIsVisible={setIsDeleteErrorModalVisible} />}
            {!isReadOnly && isConversionChooserVisible && (
                <ModalMembershipConversion
                    group={groupConversionState}
                    share={shareConversionState}
                    onClose={() => setIsConversionChooserVisible(false)}
                    onSelect={(choice) => {
                        setIsConversionChooserVisible(false);
                        if (choice === 'group') {
                            setIsConvertModalVisible(true);
                            return;
                        }
                        setShareConvertRequestKey((key) => key + 1);
                    }}
                />
            )}
            {!isReadOnly && isConvertModalVisible && (
                <ModalConvertToGroupTicket
                    setIsVisible={setIsConvertModalVisible}
                    membership={membership}
                    memberName={memberName}
                    centerId={centerId}
                    memberId={memberId}
                    createAlert={createAlert}
                />
            )}
            {!isReadOnly && isGroupSessionRegisterVisible && membership && centerId && memberId && (
                <ModalGroupTicketSessionRegister
                    setIsVisible={setIsGroupSessionRegisterVisible}
                    centerId={centerId}
                    memberId={memberId}
                    memberName={memberName}
                    membership={membership}
                    createAlert={createAlert}
                />
            )}
            {!isReadOnly && selectedGroupBookingHistory && centerId && (
                <ModalGroupBookingHistoryAction
                    centerId={centerId}
                    row={selectedGroupBookingHistory}
                    memberName={memberName}
                    createAlert={createAlert}
                    onClose={() => setSelectedGroupBookingHistory(null)}
                    onChanged={() => {
                        queryClient.invalidateQueries({
                            queryKey: ['getMembershipForMemberQuery'],
                        });
                    }}
                />
            )}

            {isChangeLogModalVisible && membershipId && (
                <Modal setIsVisible={setIsChangeLogModalVisible} isCloseButton whiteViewWidth={1120}>
                    <div style={{ paddingTop: 28, paddingBottom: 32, paddingLeft: 32, paddingRight: 72 }}>
                        <MemberMembershipChangeLog
                            targetType={source}
                            targetId={membershipId}
                            memberId={memberId}
                        />
                    </div>
                </Modal>
            )}
            {!isReadOnly && isProductChangeModalVisible && beforeMembershipTemplate && (
                <ModalMembershipChange
                    setIsVisible={setIsProductChangeModalVisible}
                    onClickCreateNewMembership={() => setIsRegisterPassModalVisible(true)}
                    beforeMembershipTemplate={beforeMembershipTemplate}
                    membershipIds={[membershipId]}
                    createAlert={createAlert}
                    onPatchRelocateMembershipSuccess={() => {
                        createAlert('수강권 상품변경 완료', '변경된 수강권 상품 정보로 갱신되었습니다.', false);
                        setIsProductChangeModalVisible(false);
                        queryClient.invalidateQueries({ queryKey: ['getMembershipForMemberQuery'] });
                        queryClient.invalidateQueries({ queryKey: ['getAllMembershipForMemberQuery'] });
                        queryClient.invalidateQueries({ queryKey: ['getMembershipTemplatesListAPI'] });
                        queryClient.invalidateQueries({ queryKey: ['getDetailMembershipAPIQuery'] });
                        queryClient.invalidateQueries({ queryKey: ['membershipChangeLog'] });
                    }}
                />
            )}
            {!isReadOnly && isRegisterPassModalVisible && (
                <ModalRegisterPass
                    modalVisible={isRegisterPassModalVisible}
                    setModalVisible={setIsRegisterPassModalVisible}
                    createAlert={createAlert}
                    onSuccessCreate={() => {
                        queryClient.invalidateQueries({ queryKey: ['getMembershipTemplatesListAPI'] });
                    }}
                />
            )}

            {/* 사업자 정보 (카드사 심사용) */}
            <div style={{ width: '100%', marginTop: 40 }}>
                <BusinessInfoFooter />
            </div>
        </div>
    );
};

export default MemberMembershipManagePage;
